import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { useTheme } from './ThemeContext';

// undefined = auth state not yet resolved (still initializing)
// null      = confirmed: no session / no value
// string    = confirmed user type
interface AuthState {
  session: Session | null | undefined;
  user: User | null | undefined;
  userType: string | null | undefined;
  isAdmin: boolean;
  loading: boolean;
  // Latches true once ALL of session + user + userType have resolved atomically.
  // Never flips back to false — route guards use this as the single ready signal.
  authHydrated: boolean;
}

interface AuthContextType extends AuthState {
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: undefined,
  user: undefined,
  userType: undefined,
  isAdmin: false,
  loading: true,
  authHydrated: false,
  signOut: async () => {},
});

function getUserTypeFromSession(session: Session | null): string | null {
  if (!session?.user) return null;
  const app = session.user.app_metadata as Record<string, unknown> | undefined;
  const meta = session.user.user_metadata as Record<string, unknown> | undefined;
  return (app?.user_type as string) || (meta?.user_type as string) || null;
}

async function fetchUserTypeFromDb(userId: string): Promise<string | null> {
  try {
    const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), 5000));
    const query = supabase
      .from('users')
      .select('user_type')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => data?.user_type || null);
    return await Promise.race([query, timeout]);
  } catch {
    return null;
  }
}

// Handles Google OAuth user setup: creates DB records for brand-new users and
// stamps twofa_verified. Must be called OUTSIDE the onAuthStateChange callback
// (deferred via setTimeout) to avoid Supabase's internal deadlock on nested auth calls.
function scheduleOAuthUserSetup(user: User): void {
  setTimeout(async () => {
    try {
      const provider = (user.app_metadata as Record<string, unknown>)?.provider as string | undefined;
      if (provider !== 'google') return;

      const meta = user.user_metadata as Record<string, unknown> | undefined;

      // Only stamp twofa_verified if it isn't already set — avoids a spurious
      // USER_UPDATED event on every page load for returning OAuth users.
      if (!meta?.twofa_verified) {
        await supabase.auth.updateUser({ data: { twofa_verified: true } });
      }

      const params = new URLSearchParams(window.location.search);
      const urlUserType = params.get('user_type');
      const validTypes = ['consultant', 'workday_services_client'];
      const resolvedType = validTypes.includes(urlUserType || '') ? urlUserType! : 'consultant';

      if (!meta?.user_type) {
        await supabase.auth.updateUser({ data: { user_type: resolvedType } });
      }

      const { data: existingUser } = await supabase
        .from('users').select('id').eq('id', user.id).maybeSingle();

      if (!existingUser) {
        const fullName = (meta?.full_name as string) || (meta?.name as string) || '';
        const firstName = (meta?.given_name as string) || fullName.split(' ')[0] || '';
        const lastName = (meta?.family_name as string) || (fullName.includes(' ') ? fullName.slice(fullName.indexOf(' ') + 1) : '') || '';

        await supabase.from('users').insert({
          id: user.id, email: user.email,
          first_name: firstName, last_name: lastName,
          oauth_provider: 'google', user_type: resolvedType,
        });

        if (resolvedType === 'workday_services_client') {
          const { data: ep } = await supabase.from('workday_client_profiles').select('id').eq('id', user.id).maybeSingle();
          if (!ep) {
            await supabase.from('workday_client_profiles').insert({
              id: user.id, first_name: firstName, last_name: lastName,
              email: user.email, oauth_provider: 'google', status: 'active', role: 'account_owner',
            });
          }
        } else {
          const { data: ep } = await supabase.from('consultant_profiles').select('id').eq('id', user.id).maybeSingle();
          if (!ep) {
            await supabase.from('consultant_profiles').insert({
              id: user.id, first_name: firstName, last_name: lastName,
              email: user.email, oauth_provider: 'google', status: 'pending', profile_complete: false,
            });
          }
        }
      }
    } catch (err) {
      console.error('Error in scheduleOAuthUserSetup:', err);
    }
  }, 0);
}

// Returns all auth fields atomically — user and userType always resolve together.
// This is the ONLY function that produces a complete auth state object.
async function resolveAuthState(
  session: Session | null,
  event?: string
): Promise<Omit<AuthState, 'loading' | 'authHydrated'>> {
  if (!session?.user) {
    return { session: null, user: null, userType: null, isAdmin: false };
  }

  let type = getUserTypeFromSession(session);
  if (!type) {
    type = await fetchUserTypeFromDb(session.user.id);
  }

  if (event === 'SIGNED_IN') {
    // Fire-and-forget — runs after the current auth event completes to avoid
    // Supabase's internal deadlock on nested auth calls (updateUser inside handler).
    scheduleOAuthUserSetup(session.user);
  }

  return {
    session,
    user: session.user,
    userType: type,
    isAdmin: type === 'admin',
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: undefined,
    user: undefined,
    userType: undefined,
    isAdmin: false,
    loading: true,
    authHydrated: false,
  });

  const { resetTheme } = useTheme();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Register listener BEFORE getSession() so we never miss an event.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        try {
          console.log('[AUTH EVENT]', event, session?.user?.id ?? null);

          // On Safari, INITIAL_SESSION can fire with session=null even when a valid
          // session exists in storage (ITP delays cookie/localStorage reads).
          // Ignore it — getSession() below is the authoritative initial source.
          if (event === 'INITIAL_SESSION' && !session) return;

          const resolved = await resolveAuthState(session, event);
          if (!mountedRef.current) return;

          setState({ ...resolved, loading: false, authHydrated: true });

          if (!session) resetTheme();
        } catch (err) {
          console.error('Error in onAuthStateChange:', err);
          if (mountedRef.current) {
            setState({ session: null, user: null, userType: null, isAdmin: false, loading: false, authHydrated: true });
          }
        }
      })();
    });

    // getSession() is the authoritative initial state — bypasses the Safari
    // INITIAL_SESSION race by reading storage directly.
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('[GET SESSION]', session?.user?.id ?? null);
      if (!mountedRef.current) return;
      if (session) {
        const resolved = await resolveAuthState(session, 'INITIAL_SESSION');
        if (!mountedRef.current) return;
        setState(prev => {
          // Don't overwrite if the listener already resolved this same user.
          if (prev.authHydrated && prev.user?.id === session.user.id) return prev;
          return { ...resolved, loading: false, authHydrated: true };
        });
      } else {
        setState(prev => {
          if (prev.authHydrated) return prev;
          return { session: null, user: null, userType: null, isAdmin: false, loading: false, authHydrated: true };
        });
      }
    }).catch(() => {
      if (mountedRef.current) {
        setState(prev => {
          if (prev.authHydrated) return prev;
          return { session: null, user: null, userType: null, isAdmin: false, loading: false, authHydrated: true };
        });
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    resetTheme();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ ...state, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
